import { TERMS_ACCEPTANCE_REQUIRED_CODE } from '@featit/shared/legal';

/**
 * I-2273 (AC-TA19): the API refuses a person who has not accepted the terms in force (403
 * `terms_acceptance_required`), for example when the version changed while the app was open. The web
 * turns that refusal into its terms screen: the own account is asked for again, its
 * `termsAcceptanceRequired` turns true, and `ProtectedRoute` draws `TermsAcceptanceScreen`. It never
 * signs anybody out.
 */
export const TERMS_REFUSED_EVENT = 'featit:terms-refused';

/** Whether `error` is the API's refusal for terms not yet accepted. */
export function isTermsRefusal(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { code?: unknown } } })
    ?.response;
  return response?.status === 403 && response.data?.code === TERMS_ACCEPTANCE_REQUIRED_CODE;
}

/** Said by the API client when a call was refused for the terms. */
export function publishTermsRefusal(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(TERMS_REFUSED_EVENT));
}

/**
 * Asks for the own account again on each refusal, one ask at a time: a page whose ten calls are all
 * refused asks once. Returns how to stop.
 */
export function followTermsRefusals(reloadOwnAccount: () => Promise<unknown>): () => void {
  if (typeof window === 'undefined') return () => undefined;
  let asking = false;
  const listener = () => {
    if (asking) return;
    asking = true;
    void reloadOwnAccount()
      .catch(() => undefined)
      .finally(() => {
        asking = false;
      });
  };
  window.addEventListener(TERMS_REFUSED_EVENT, listener);
  return () => window.removeEventListener(TERMS_REFUSED_EVENT, listener);
}
